<template>
  <div class="stepper-section">
    <div class="stepper-container">
      <!-- Progress Bar -->
      <div class="progress-bar">
        <div class="progress-track">
          <div class="progress-fill" :style="{ width: progressPercentage + '%' }">
            <div class="progress-glow"></div>
          </div>
        </div>
      </div>

      <!-- Steps -->
      <div class="steps-wrapper">
        <div
          v-for="(step, index) in steps"
          :key="step.id"
          :class="['step-item', {
            'active': currentStep === step.id,
            'completed': currentStep > step.id,
            'clickable': step.id < currentStep
          }]"
          @click="goToStep(step.id)"
        >
          <!-- Step Circle -->
          <div class="step-circle">
            <div class="circle-inner">
              <transition name="icon-swap" mode="out-in">
                <div v-if="currentStep > step.id" class="checkmark" key="check">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
                       stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </div>
                <div v-else :key="step.id" class="step-number-display">
                  {{ step.id }}
                </div>
              </transition>
            </div>

            <!-- Pulsing ring for active step -->
            <div v-if="currentStep === step.id" class="pulse-ring"></div>
          </div>

          <!-- Step Content -->
          <div class="step-content">
            <span class="step-badge">{{ step.badge || 'Step' }}</span>
            <h4 class="step-title">{{ step.title }}</h4>
            <p class="step-description">{{ step.description }}</p>
          </div>

          <!-- Connector Line (desktop / tablet only) -->
          <div
            v-if="index < steps.length - 1"
            :class="['step-connector', { 'completed': currentStep > step.id }]"
          >
            <div class="connector-line"></div>
            <div class="connector-dot" :class="{ 'active': currentStep > step.id }"></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'StepperProgress',
  props: {
    currentStep: {
      type: Number,
      required: true,
      default: 1
    }
  },
  data() {
    return {
      steps: [
        { id: 1, title: 'Select & Book', description: 'Choose your experience', badge: 'Step 1', route: 'MakeGuestBooking' },
        { id: 2, title: 'Review & Checkout', description: 'Confirm your selections', badge: 'Step 2', route: 'GuestCheckout' },
        { id: 3, title: 'Payment', description: 'Secure checkout', badge: 'Step 3', route: 'GuestPaymentInformation' },
        { id: 4, title: 'Confirmation', description: 'Booking complete', badge: 'Step 4', route: 'GuestSuccess' }
      ]
    }
  },
  computed: {
    progressPercentage() {
      return ((this.currentStep - 1) / (this.steps.length - 1)) * 100
    }
  },
  methods: {
    goToStep(stepId) {
      const targetStep = this.steps.find(s => s.id === stepId)
      if (targetStep && targetStep.route && this.currentStep !== stepId) {
        if (stepId <= this.currentStep) {
          this.$router.push({ name: targetStep.route })
        }
      }
    }
  }
}
</script>

<style scoped>
/* ─── TOKENS ─── */
.stepper-section {
  --brand: #74a533;
  --brand-light: #8bc34a;
  --brand-soft: rgba(116, 165, 51, 0.10);
  --brand-ring: rgba(116, 165, 51, 0.28);
  --ink-900: #1a1a1a;
  --ink-700: #495057;
  --ink-500: #6c757d;
  --ink-300: #adb5bd;
  --line: #e9ecef;
  --line-soft: rgba(0, 0, 0, 0.06);
  --surface: #fff;

  padding: 20px 0 30px;
  background: transparent;
}

.stepper-container {
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 20px;
}

/* ─── PROGRESS BAR ─── */
.progress-bar {
  margin-bottom: clamp(20px, 3vw, 40px);
  padding: 0 8px;
}

.progress-track {
  height: 6px;
  background: var(--line-soft);
  border-radius: 99px;
  overflow: hidden;
  position: relative;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, var(--brand), var(--brand-light), var(--brand));
  background-size: 200% 100%;
  border-radius: 99px;
  transition: width 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
  position: relative;
  animation: shimmer 2.4s ease-in-out infinite;
}

@keyframes shimmer {
  0%, 100% { background-position: 0% 50%; }
  50%      { background-position: 100% 50%; }
}

.progress-glow {
  position: absolute;
  top: -2px;
  right: -10px;
  width: 30px;
  height: 10px;
  background: var(--brand-ring);
  filter: blur(8px);
  border-radius: 50%;
}

/* ─── STEPS WRAPPER (desktop default) ─── */
.steps-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  position: relative;
}

.step-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1 1 0;
  min-width: 0;
  position: relative;
  cursor: default;
  transition: transform 0.25s ease;
}

.step-item.clickable { cursor: pointer; }

.step-item.clickable:focus-visible {
  outline: 2px solid var(--brand);
  outline-offset: 4px;
  border-radius: 12px;
}

.step-item.clickable:hover .step-title { color: var(--brand); }

/* ─── STEP CIRCLE ─── */
.step-circle {
  width: clamp(44px, 5.5vw, 60px);
  height: clamp(44px, 5.5vw, 60px);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--surface);
  border: 2px solid var(--line);
  position: relative;
  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  z-index: 2;
  flex-shrink: 0;
}

.circle-inner {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
}

.step-number-display {
  font-size: clamp(14px, 1.6vw, 20px);
  font-weight: 700;
  color: var(--ink-300);
  transition: color 0.3s ease;
}

.step-item.active .step-circle {
  border-color: var(--brand);
  background: var(--brand);
  transform: scale(1.06);
  box-shadow: 0 6px 22px rgba(116, 165, 51, 0.35);
}

.step-item.active .step-number-display { color: #fff; }

.step-item.completed .step-circle {
  border-color: var(--brand);
  background: var(--brand);
  box-shadow: 0 4px 16px rgba(116, 165, 51, 0.25);
}

.step-item.completed .checkmark {
  color: #fff;
  animation: checkmarkPop 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

@keyframes checkmarkPop {
  0%   { transform: scale(0);   opacity: 0; }
  100% { transform: scale(1);   opacity: 1; }
}

.checkmark svg {
  width: clamp(18px, 2vw, 24px);
  height: clamp(18px, 2vw, 24px);
}

/* ─── PULSE RING ─── */
.pulse-ring {
  position: absolute;
  inset: -4px;
  border-radius: 50%;
  border: 3px solid var(--brand-ring);
  animation: pulse 1.6s ease-out infinite;
  pointer-events: none;
}

@keyframes pulse {
  0%   { transform: scale(0.85); opacity: 0.85; }
  100% { transform: scale(1.4);  opacity: 0; }
}

/* ─── STEP CONTENT ─── */
.step-content {
  margin-top: 12px;
  text-align: center;
  padding: 0 6px;
  transition: all 0.3s ease;
  min-width: 0;
}

.step-badge {
  display: inline-block;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: var(--ink-300);
  background: var(--line-soft);
  padding: 3px 10px;
  border-radius: 99px;
  margin-bottom: 6px;
  transition: all 0.3s ease;
}

.step-item.active .step-badge,
.step-item.completed .step-badge {
  color: var(--brand);
  background: var(--brand-soft);
}

.step-title {
  font-size: clamp(12px, 1.3vw, 14.5px);
  font-weight: 600;
  color: var(--ink-700);
  margin: 0 0 3px;
  line-height: 1.25;
  transition: color 0.3s ease;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

.step-item.active .step-title,
.step-item.completed .step-title {
  color: var(--ink-900);
}

.step-description {
  font-size: 11.5px;
  color: var(--ink-300);
  margin: 0;
  line-height: 1.3;
  transition: color 0.3s ease;
}

.step-item.active .step-description,
.step-item.completed .step-description {
  color: var(--ink-500);
}

.step-connector {
  position: absolute;
  top: calc(clamp(44px, 5.5vw, 60px) / 2 - 1px);
  left: 60%;
  right: -40%;
  height: 2px;
  background: var(--line);
  z-index: 1;
  transition: background 0.5s ease;
}

.step-connector.completed { background: var(--brand); }

.connector-line {
  width: 100%;
  height: 100%;
  background: inherit;
}

.connector-dot {
  position: absolute;
  right: -5px;
  top: -4px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: var(--line);
  border: 2px solid var(--surface);
  box-shadow: 0 0 0 2px var(--line);
  transition: all 0.4s ease;
}

.connector-dot.active {
  background: var(--brand);
  box-shadow: 0 0 0 2px var(--brand);
}

/* ─── ICON SWAP TRANSITION ─── */
.icon-swap-enter-active,
.icon-swap-leave-active {
  transition: all 0.25s ease;
}

.icon-swap-enter-from {
  opacity: 0;
  transform: scale(0.5) rotate(-90deg);
}

.icon-swap-leave-to {
  opacity: 0;
  transform: scale(0.5) rotate(90deg);
}

@media (prefers-reduced-motion: reduce) {
  .progress-fill,
  .pulse-ring,
  .step-circle,
  .step-connector,
  .icon-swap-enter-active,
  .icon-swap-leave-active {
    animation: none !important;
    transition: none !important;
  }
}

@media (max-width: 1023px) {
  .step-description { display: none; }
}

/* ─── PHONE: compact horizontal indicator (light) ─── */
@media (max-width: 767px) {
  .stepper-section {
    padding: 8px 0 12px;
  }

  .stepper-container {
    padding: 0 12px;
  }

  .progress-bar {
    margin-bottom: 12px;
    padding: 0;
  }

  .progress-track { height: 4px; }

  /* Keep the row, drop the per-step card styling */
  .steps-wrapper {
    flex-direction: row;
    gap: 0;
    align-items: flex-start;
    justify-content: space-between;
  }

  .step-item {
    flex: 1 1 0;
    flex-direction: column;
    align-items: center;
    width: auto;
    padding: 0;
    background: transparent;
    border: none;
    border-radius: 0;
    box-shadow: none;
    gap: 0;
  }

  .step-item.active,
  .step-item.completed {
    background: transparent;
    border: none;
    box-shadow: none;
  }

  .step-item.clickable:hover {
    transform: none;
    border: none;
    box-shadow: none;
  }

  .step-circle {
    width: 30px;
    height: 30px;
    border-width: 2px;
  }

  .step-number-display { font-size: 12px; }
  .checkmark svg { width: 14px; height: 14px; }

  /* Active pulse: smaller + softer */
  .pulse-ring { inset: -2px; border-width: 2px; }

  .step-content {
    margin-top: 6px;
    text-align: center;
    flex: none;
    width: 100%;
    padding: 0 2px;
  }

  .step-badge { display: none; }

  .step-title {
    font-size: 10.5px;
    line-height: 1.2;
    margin: 0;
    font-weight: 600;
    white-space: normal;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }

  .step-description { display: none; }

  /* Bring back a thin connector between circles */
  .step-connector {
    display: block;
    position: absolute;
    top: 14px;
    left: 50%;
    right: -50%;
    height: 2px;
    background: var(--line);
    z-index: 1;
  }

  .step-connector.completed { background: var(--brand); }

  .connector-dot { display: none; }
}

@media (max-width: 380px) {
  .stepper-container { padding: 0 8px; }

  .step-title { font-size: 9.5px; }

  .step-circle {
    width: 26px;
    height: 26px;
  }

  .step-number-display { font-size: 11px; }
  .checkmark svg { width: 12px; height: 12px; }

  .step-connector { top: 12px; }
}

@media (min-width: 1440px) {
  .stepper-container { max-width: 1100px; }
  .progress-bar { padding: 0 12px; }
  .step-content { padding: 0 10px; }
}
</style>